import type { PacesetterTheme } from '../api/types';

/**
 * Base (preset) colours for the Pacesetter terminal — the vt220 look before
 * any per-field custom override. Matches the preset list in the design spec
 * (2026-07-16-pacesetter-terminal-design.md §5); "custom" has no preset of
 * its own (falls back to the "green" baseline) since a truly custom theme is
 * expected to set all three override fields.
 *
 * "app" ("Según la app") is meant to follow the webUI's own light/dark
 * toggle, but this table is plain data consumed by the pure `themeToXterm`
 * helper below (deliberately no DOM/document access, so it stays trivially
 * testable) — it resolves to a light baseline here. A live app-theme-aware
 * override, if ever wanted, belongs at the render call site, not this table.
 */
const PRESET_COLORS: Record<string, { background: string; foreground: string; cursor: string }> = {
  green:    { background: '#000000', foreground: '#33ff33', cursor: '#33ff33' },
  amber:    { background: '#000000', foreground: '#ffb000', cursor: '#ffb000' },
  dec:      { background: '#1a1aa6', foreground: '#ffffff', cursor: '#ffffff' },
  light:    { background: '#ffffff', foreground: '#111827', cursor: '#111827' },
  contrast: { background: '#000000', foreground: '#ffffff', cursor: '#ffffff' },
  app:      { background: '#ffffff', foreground: '#111827', cursor: '#111827' },
  custom:   { background: '#000000', foreground: '#33ff33', cursor: '#33ff33' },
};

export interface XtermThemeConfig {
  theme: { background: string; foreground: string; cursor: string };
  fontSize: number;
}

/**
 * Maps a PacesetterTheme (the wire/config shape) to the subset of xterm.js
 * `ITerminalOptions` the terminal page + the config page's live preview both
 * need. `background`/`foreground`/`cursor` on the theme are custom overrides
 * that win over the preset when non-empty (matches internal/paceterm.Theme's
 * doc comment: "empty = preset default"); an unrecognized/missing preset
 * falls back to "green". `font_size` of 0 (unset) falls back to 14px.
 *
 * Shared by Pacesetter.tsx (applies it to the live xterm instance) and
 * Config.tsx (the appearance section's live preview) so the two never drift.
 */
export function themeToXterm(t: PacesetterTheme): XtermThemeConfig {
  const base = PRESET_COLORS[t.preset] ?? PRESET_COLORS.green;
  return {
    theme: {
      background: t.background || base.background,
      foreground: t.foreground || base.foreground,
      cursor: t.cursor || base.cursor,
    },
    fontSize: t.font_size || 14,
  };
}
